import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import PageShell, { PageHeader } from "@/components/PageShell";
import Seo from "@/components/Seo";
import { Megaphone, Users, Tv, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const highlights = [
  { icon: Users,    title: "Engaged diaspora", body: "Reach a growing base of South Asian households across the US, Canada, UK and Europe." },
  { icon: Tv,       title: "Premium context",  body: "Your brand runs beside live cricket, prime-time serials and evening news — appointment TV moments." },
  { icon: TrendingUp, title: "Full-funnel",     body: "From on-screen sponsorships to newsletter and app placements — one media plan, many touchpoints." },
];

const initial = { name: "", company: "", email: "", phone: "", budget: "", goals: "" };

const AdvertisePage = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/advertise`, {
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        budget: form.budget || null,
        goals: form.goals.trim() || null,
      });
      const mail = `mailto:advertising@nematv.com?subject=${encodeURIComponent("Advertising enquiry — " + (form.company || form.name))}&body=${encodeURIComponent(`Name: ${form.name}\nCompany: ${form.company}\nEmail: ${form.email}\nPhone: ${form.phone}\nBudget: ${form.budget}\n\nGoals:\n${form.goals}`)}`;
      window.location.href = mail;
      setStatus({ state: "success", msg: "Thanks — we've received your enquiry and opened your mail client to send us more detail. We reply within 1 business day." });
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        msg: typeof detail === "string" ? detail : "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <PageShell>
      <Seo
        title="Advertise with Nema TV"
        description="Reach the global diaspora through Nema TV — live TV, newsletter and in-app placements."
        path="/advertise"
        schemas={[{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",      item: (process.env.REACT_APP_SITE_URL || "https://nematv.com") + "/" },
            { "@type": "ListItem", position: 2, name: "Advertise", item: (process.env.REACT_APP_SITE_URL || "https://nematv.com") + "/advertise" },
          ],
        }]}
      />
      <PageHeader
        eyebrow="Media kit"
        title="Advertise where home meets prime-time."
        subtitle="Nema TV connects the global diaspora with the channels, sport and stories they grew up with. Put your brand in the middle of that moment."
      />

      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
          {highlights.map((h, i) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-white/10 bg-[#0F0A19] p-6"
              data-testid={`advertise-highlight-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 inline-flex items-center justify-center mb-4">
                <h.icon className="w-4 h-4 text-[#22D3EE]" />
              </div>
              <div className="font-display text-xl font-bold">{h.title}</div>
              <div className="mt-2 text-sm text-white/60">{h.body}</div>
            </motion.div>
          ))}
        </div>

        <form
          onSubmit={submit}
          data-testid="advertise-form"
          className="relative gradient-border p-6 md:p-10"
        >
          <div className="relative">
            <div className="flex items-center gap-3 mb-6">
              <Megaphone className="w-5 h-5 text-[#22D3EE]" />
              <div className="text-xs uppercase tracking-[0.25em] text-white/60">Tell us about your brand</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="flex flex-col gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-white/60">Your name</span>
                <input
                  data-testid="advertise-name"
                  required value={form.name} onChange={update("name")}
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE] transition"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-white/60">Company</span>
                <input
                  data-testid="advertise-company"
                  value={form.company} onChange={update("company")}
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE] transition"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-white/60">Work email</span>
                <input
                  data-testid="advertise-email"
                  required type="email" value={form.email} onChange={update("email")}
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE] transition"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-white/60">Phone (optional)</span>
                <input
                  data-testid="advertise-phone"
                  value={form.phone} onChange={update("phone")}
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE] transition"
                />
              </label>
              <label className="flex flex-col gap-2 md:col-span-2">
                <span className="text-xs uppercase tracking-[0.2em] text-white/60">Budget (optional)</span>
                <select
                  data-testid="advertise-budget"
                  value={form.budget} onChange={update("budget")}
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE] transition"
                >
                  <option value="" className="bg-[#0F0A19]">Prefer not to say</option>
                  <option value="lt_5k" className="bg-[#0F0A19]">Under $5k</option>
                  <option value="5k_25k" className="bg-[#0F0A19]">$5k – $25k</option>
                  <option value="25k_100k" className="bg-[#0F0A19]">$25k – $100k</option>
                  <option value="gt_100k" className="bg-[#0F0A19]">$100k+</option>
                </select>
              </label>
              <label className="flex flex-col gap-2 md:col-span-2">
                <span className="text-xs uppercase tracking-[0.2em] text-white/60">Campaign goals</span>
                <textarea
                  data-testid="advertise-goals"
                  rows={4} value={form.goals} onChange={update("goals")}
                  placeholder="What are you launching? Which audiences and formats interest you?"
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-[#22D3EE] transition resize-none"
                />
              </label>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-white/40">
                We&apos;ll reply within 1 business day with the current rate card and formats.
              </p>
              <button
                type="submit"
                data-testid="advertise-submit"
                disabled={status.state === "loading"}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] text-white px-8 py-3.5 text-sm font-semibold hover:shadow-[0_0_30px_rgba(34,211,238,0.45)] transition-shadow disabled:opacity-60"
              >
                {status.state === "loading" ? "Sending…" : "Request the media kit"}
              </button>
            </div>

            {status.state === "success" && (
              <div data-testid="advertise-success" className="mt-6 rounded-xl border border-[#22D3EE]/30 bg-[#22D3EE]/10 text-[#a7f3f9] px-4 py-3 text-sm">
                {status.msg}
              </div>
            )}
            {status.state === "error" && (
              <div data-testid="advertise-error" className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 px-4 py-3 text-sm">
                {status.msg}
              </div>
            )}
          </div>
        </form>
      </div>
    </PageShell>
  );
};

export default AdvertisePage;
